import Head from 'next/head';
import { NextSeo } from 'next-seo';
import { Content } from '@/screens/Content';

//logos
import Logo from '@/public/images/logos/LoadsmartLogo.svg';

// data
const heroImages = {
  xxlarge: 'images/content/hero/hero-loadsmart-01-xxlg@2x.jpg',
  large: 'images/content/hero/hero-loadsmart-01-lg@2x.jpg',
  medium: 'images/content/hero/hero-loadsmart-01-md@2x.jpg',
  small: 'images/content/hero/hero-loadsmart-01-sm@2x.jpg',
};

const contentCopy = {
  date: `February 23, 2023`,
  title: `What is drop-and-hook freight?`,
  slug: `what-is-drop-and-hook-freight`,
  highlight: (
    <>
      Trucking companies and their shipper customers are always looking for ways
      to be more efficient and reduce operating costs. An opportunity to do that
      can have significant impacts, is during the process of loads being
      delivered to or picked up at the dock. At this point in the process
      everything needs to go right to ensure loads move on time and no delays
      occur.
    </>
  ),
  copy: (
    <>
      <p>
        One of the most effective ways to gain all the benefits for both
        carriers and shippers is through a procedure called “drop-and-hook.” If
        you are unfamiliar with the term, drop-and-hook describes a way of
        picking up a truckload of freight differently than how a typical live
        load works. In basic terms, it describes when a driver must wait on-site
        for the freight to be loaded into their trailer. Conversely, the driver
        doesn’t have to wait for a pre-loaded drop-and-hook trailer. Here, the
        driver arrives at the shipper’s facility, and their empty trailer is
        “dropped” in the yard to be loaded later. The driver can then “hook and
        go” with a trailer that’s already loaded and get on the road. Below
        we’ll explain the advantages of dop-and-hook loads for both carriers and
        shippers.
      </p>
      <nav>
        <ul>
          <li>
            <a href="#how-does-the-drop-and-hook-work">
              How does the drop-and-hook work?
            </a>
          </li>
          <li>
            <a href="#who-is-drop-and-hook-different-from-live-load">
              How is drop-and-hook different from live load?
            </a>
          </li>
          <li>
            <a href="#why-is-drop-and-hook-freight-good-for-owner-operators-and-large-carrier-fleets">
              Why is drop-and-hook freight good for owner-operators and large
              carrier fleets?
            </a>
          </li>
          <li>
            <a href="#how-can-carriers-find-drop-and-hook-loads">
              How can carriers find drop-and-hook loads?
            </a>
          </li>
          <li>
            <a href="#loadsmart-gives-carriers-access">
              Loadsmart gives carriers access to drop-and-hook loads
            </a>
          </li>
        </ul>
      </nav>
      <h3 id="how-does-the-drop-and-hook-work">
        How does the drop-and-hook work?
      </h3>
      <p>
        For drop-and-hook to work, there needs to be a lot of coordination
        between the trucking company and the shipper. When the two can work in
        sync, the benefits far exceed any extra effort.
      </p>
      <p>
        The goal for the driver is to make the process as quick as possible.
        Time is money for drivers; the less time they spend at a dock, the
        better. The process isn’t always perfect, and there can be delays for
        the driver if things don’t go according to plan due to other issues out
        of their control. For example, loaded trailers might not be ready or in
        the location the driver was expecting. Or, it could be hidden behind
        other trailers or even stored in an offsite lot. Drivers may need help
        from the facility to move it into a position where it can be accessed.
        Loaded trailers can also be challenging to hook up due to equipment
        issues. Many drivers, however, will say any of these issues are worth it
        if they can avoid waiting around for loading and unloading at the dock.
        Once a process for communication is worked out between the carrier and
        shipper to keep track of what equipment is on-site at the facility, and
        for what load each will be used for next, the benefits start to emerge.
      </p>

      <h3 id="who-is-drop-and-hook-different-from-live-load">
        How is drop-and-hook different from live load?
      </h3>
      <p>
        Finding drop-and-hook opportunities is not easy at the beginning. As we
        already noted, it takes a consistent volume of freight from a single
        shipper to sustain itself and ensure an adequate flow of trailers are
        making it to the facility for preloading. The problem is that not every
        shipper can offer this. Or, if they do, they may be unaware of the
        benefits of drop-and-hook to their operation. This means it’s sometimes
        necessary for trucking companies (smaller owner-operators and large
        fleets alike) to be proactive in finding these mutually beneficial
        opportunities.
      </p>
      <p>
        The best place to find drop-and-hook arrangements for carriers is often
        by talking with companies near their headquarters or terminals, so there
        is easy access to equipment and drivers. Remember, the shippers benefit
        as much as you do with the arrangement, yet they may not be as aware of
        what they stand to gain—so it’s on you to help them understand.
      </p>

      <h3 id="loadsmart-gives-carriers-access">
        Loadsmart gives carriers access to drop-and-hook loads
      </h3>
      <p>
        Another tactic is to rely on drop-and-hook load boards which allow
        trucking companies to find shippers who already understand the many
        benefits of having trailers spotted at their facility. This is a quick
        way to establish this type of load volume. The good news is that this
        opportunity builds on itself, so that carriers can become reputable as
        the best drop-and-hook trucking company in a city or region.
      </p>
      <p>
        The benefits of drop-and-hook freight to trucking companies and shippers
        are significant and address many challenges both face related to
        creating more operational efficiency. This includes better utilization
        of employee resources and fixed assets (e.g., tractors, trailers,
        warehouses, and dock space).
      </p>
    </>
  ),
};

const sidebarLinks = [
  {
    label: 'Three Tips to Make Sure Your RFP Process is Structurally Sound.',
    url: 'https://blog.loadsmart.com/2021/12/14/three-tips-to-make-sure-your-rfp-process-is-structurally-sound/',
    external: true,
  },
  {
    label:
      'Leveraging Managed Transportation to Build a Resilient Network Strategy.',
    url: 'https://blog.loadsmart.com/2022/06/01/learn-with-loadsmart-leveraging-managed-transportation-to-build-a-resilient-network-strategy/',
    external: true,
  },
  {
    label: 'How To Answer Common Questions About Today’s Market.',
    url: 'https://blog.loadsmart.com/2022/01/19/hurdling-supply-chain-pain-how-to-answer-common-questions-about-todays-market/',
    external: true,
  },
];

const bannerContent = {
  logo: <Logo />,
  title: (
    <>
      Enable Drop and Hook Freight for your Shipping Needs with Loasmart.
      <br />
      Contact Loadsmart to learn more about how drop-and-hook freight can
      provide these benefits to your trucking company.
    </>
  ),
  cta: 'Learn more',
  url: 'https://loadsmart.com/shipper/',
};

export default function WhatIsDropAndHook() {
  return (
    <>
      <Head>
        <meta
          type="keywords"
          content="Drop-and-hook, drop load, drop-and-hook loads, drop a load, hook and go, drop and hook loads for owner operators, what is drop and hook, drop and hook trucking, hook and drop loads, what does drop and hook mean, hook and drop, drop trailer program, drop-and-hook freight, drop-and-hook trucking companies, drop and hook load boards, drop and hook trailer, best drop-and-hook trucking companies, how to find drop-and-hook loads"
        />
      </Head>
      <NextSeo
        nofollow={process.env.NOINDEX_FLAG}
        noindex={process.env.NOINDEX_FLAG}
        title="What is Drop and Hook Freight | RFP Guide | Loadsmart"
        description="What is drop-and-hook freight for carriers looking to book and source freight loads for the logistics industry? Learn how this load is managed across the shipment's lifecycle."
        canonical="https://loadsmart.com/content/what-is-drop-and-hook-freight/"
        openGraph={{
          url: 'https://loadsmart.com/content/what-is-drop-and-hook-freight/',
        }}
      />

      <Content
        hero={heroImages}
        content={contentCopy}
        sidebar={sidebarLinks}
        banner={bannerContent}
      />
    </>
  );
}
